import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Trash2, RotateCcw, X } from "lucide-react";

export function CestinoButton({ entity, onChange, size = "h-10 text-sm" }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback((withItems = false) => {
    api.get(`/cestino/${entity}`).then((r) => {
      setCount(r.data.items.length);
      setLabel(r.data.label);
      setRetention(r.data.retention_days);
      if (withItems) setItems(r.data.items);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [entity]);

  useEffect(() => { refresh(false); }, [refresh]);

  const openDialog = () => { setOpen(true); setLoading(true); refresh(true); };

  const restore = async (id) => {
    try {
      await api.post(`/cestino/${entity}/${id}/ripristina`);
      toast.success("Elemento ripristinato");
      refresh(true);
      onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const purge = async (id, nome) => {
    if (!window.confirm(`Eliminare definitivamente "${nome}"?\nL'operazione NON è reversibile.`)) return;
    try {
      await api.delete(`/cestino/${entity}/${id}`);
      toast.success("Eliminato definitivamente");
      refresh(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const empty = async () => {
    if (!window.confirm("Svuotare il cestino?\nTutti gli elementi verranno eliminati definitivamente. L'operazione NON è reversibile.")) return;
    try {
      const r = await api.post(`/cestino/${entity}/svuota`);
      toast.success(`${r.data.eliminati} element${r.data.eliminati === 1 ? "o" : "i"} eliminat${r.data.eliminati === 1 ? "o" : "i"} definitivamente`);
      refresh(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <Button data-testid={`cestino-btn-${entity}`} variant="outline" className={`${size} relative`} onClick={openDialog} title="Cestino">
        <Trash2 className="w-4 h-4 mr-1.5" /> Cestino
        {count > 0 && (
          <span data-testid={`cestino-count-${entity}`}
            className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-xs font-bold">
            {count}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2"><Trash2 className="w-6 h-6" /> Cestino — {label}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Gli elementi eliminati restano qui e sono ripristinabili. Vengono svuotati automaticamente dopo {retention} giorni.
          </p>
          {loading ? (
            <div className="py-8 text-center text-slate-400">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-slate-400" data-testid={`cestino-empty-${entity}`}>Il cestino è vuoto.</div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} data-testid={`cestino-item-${it.id}`} className="flex items-center gap-2 p-3 border-2 rounded-lg min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{it.nome}</div>
                    {it.sottotitolo && <div className="text-xs text-slate-500 truncate">{it.sottotitolo}</div>}
                  </div>
                  <Button data-testid={`cestino-restore-${it.id}`} variant="outline" size="sm"
                    className="h-9 text-green-700 border-green-300 shrink-0" onClick={() => restore(it.id)}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Ripristina
                  </Button>
                  <Button data-testid={`cestino-purge-${it.id}`} variant="outline" size="sm"
                    className="h-9 text-red-700 border-red-300 shrink-0" title="Elimina definitivamente" onClick={() => purge(it.id, it.nome)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button data-testid={`cestino-empty-btn-${entity}`} variant="outline"
                className="w-full h-10 text-red-700 border-red-300 mt-2" onClick={empty}>
                <Trash2 className="w-4 h-4 mr-1.5" /> Svuota cestino
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
